/** Shared contract for the /evidence/graph-lab prototypes: the payload
 *  shape served by /api/evidence/graph plus the validated category palette.
 *
 *  Palette provenance: 6-slot subset of the dataviz reference palette,
 *  chosen by enumerating all 6-of-8 subsets and keeping the one with the
 *  best worst-pair separation (all-pairs CVD ΔE 6.9 deutan, normal-vision
 *  floor 15.6, on white). CVD 6.9 sits in the 6–8 floor band, which is
 *  legal only with secondary encoding — nodes carry direct text labels and
 *  a legend, which satisfies it. The site accent #990000 is deliberately
 *  NOT a category color: it is reserved for selection/interaction so a
 *  highlighted node never impersonates a sixth category.
 */

export interface GraphNode {
  term: string;
  category: number;
  pages: number;
}

export interface GraphEdge {
  source: string;
  target: string;
  pages: number;
}

export interface GraphCategory {
  id: number;
  label: string;
}

export interface GraphData {
  categories: GraphCategory[];
  nodes: GraphNode[];
  edges: GraphEdge[];
}

/** A selection made in either renderer: a term (node click) optionally
 *  narrowed to pages also mentioning a second term (edge click). */
export interface GraphSelection {
  term: string;
  alsoTerm?: string;
}

export const ACCENT = "#990000";

/** Slots assigned in category-id order, fixed — never re-derived from
 *  whatever categories happen to be visible. */
const CATEGORY_SLOTS = [
  "#2a78d6", // 1 Cultural practices
  "#008300", // 2 Human rights activism
  "#eda100", // 3 Transitional justice
  "#1baf7a", // 4 Geography
  "#4a3aa7", // 5 Social actors
  "#e34948", // 6 Institutions & services
];

export const categoryColor = (category: number): string =>
  CATEGORY_SLOTS[(category - 1) % CATEGORY_SLOTS.length] || "#5a6472";

/** Ink for text set on an arbitrary background color. White is
 *  preferred whenever it stays readable (>= 3:1) — mid-tone saturated
 *  backgrounds read better with white even where dark ink measures
 *  slightly higher — and dark ink serves the genuinely light
 *  backgrounds where white washes out. */
const DARK_INK = "#212529";
export const readableInkOn = (hex: string): string => {
  const raw = hex.replace("#", "");
  const chan = (i: number) => {
    const c = parseInt(raw.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
  const contrastWhite = 1.05 / (luminance + 0.05);
  return contrastWhite >= 3 ? "#ffffff" : DARK_INK;
};

/** Highlight style on a category color. The two light hues (amber,
 *  teal) fail WCAG contrast with white text — they carry dark ink;
 *  the rest carry white. */
export const categoryMarkStyle = (
  category: number,
): { background: string; color: string; borderColor: string } => {
  const background = categoryColor(category);
  // borderColor matches the fill so hosts with their own border rules
  // (the archive's pagination buttons) can't frame the pill in red.
  return {
    background,
    color: readableInkOn(background),
    borderColor: background,
  };
};

/** The lexicon stores every term lowercase, but Geography terms are
 *  proper nouns (countries, cities, provinces) — title-case those for
 *  display only; API queries keep the raw lowercase term. */
export const properNounCategories = (categories: GraphCategory[]): Set<number> =>
  new Set(categories
    // "Geography" before the September 2026 revision, "Regions" after
    .filter((c) => /geograph|region/i.test(c.label))
    .map((c) => c.id));

// Joining words stay lowercase inside a proper name ("Bosnia and
// Herzegovina", "Democratic Republic of the Congo"), except at the start.
const NAME_PARTICLES = new Set(["and", "of", "the", "de", "del", "la", "y"]);

export const displayTerm = (term: string, proper: boolean): string =>
  (proper
    ? term.replace(/\p{L}+/gu, (w, offset: number) =>
      (offset > 0 && NAME_PARTICLES.has(w)
        ? w
        : w[0].toUpperCase() + w.slice(1)))
    : term);

export interface RendererProps {
  data: GraphData;
  hiddenCategories: Set<number>;
  selection: GraphSelection | null;
  onSelect: (selection: GraphSelection | null) => void;
}
